import React, { useState, useRef, useMemo } from "react";
import Header from "./../Layout/Header";
import PageTitle from "./../Pages/PageTitle";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { Toast } from "primereact/toast";
import axios from "axios";
import { useSelector } from "react-redux";
import { logout } from "../../redux/slices/userSlice";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import id from "../../images/id.png";
import SettingMenu from "../component/SettingMenu";
import { useTranslation } from "react-i18next";

const API_BASE =
  (process.env.REACT_APP_API_BASE &&
    process.env.REACT_APP_API_BASE.replace(/\/$/, "")) ||
  "https://miracleapi.com";

const SettingsSecurity = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userInfo = useSelector((state) => state.user.user.data);
  console.log("userinfo: ", userInfo);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const toast = useRef(null);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const toggleModal = () => setModalOpen((s) => !s);

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) {
      setSelectedFile(null);
      setPreviewUrl(null);
      return;
    }
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const submitImageFile = async () => {
    if (!selectedFile) {
      toast.current?.show({
        severity: "error",
        summary: "Upload Error",
        detail: "Please choose an image first.",
      });
      return;
    }

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const res = await axios.post(
        `${API_BASE}/user/${userInfo.id}/postImage`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const imageUrl = res?.data;

      toast.current?.show({
        severity: "success",
        summary: "Upload Success",
        detail: `Successfully uploaded image${imageUrl ? `: ${imageUrl}` : ""}`,
      });

      setSelectedFile(null);
      setPreviewUrl(null);
      toggleModal();
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Upload Error",
        detail:
          error?.response?.data?.message ||
          "Failed to upload image to the server.",
      });
    }
  };

  const rules = useMemo(
    () => ({
      length: newPassword.length >= 8,
      upper: /[A-Z]/.test(newPassword),
      lower: /[a-z]/.test(newPassword),
      number: /[0-9]/.test(newPassword),
      special: /[^A-Za-z0-9]/.test(newPassword),
      notReuse:
        currentPassword && newPassword && currentPassword !== newPassword,
      confirmMatch:
        newPassword === confirmPassword && confirmPassword.length > 0,
    }),
    [currentPassword, newPassword, confirmPassword]
  );

  const handleDeleteImage = async () => {
    try {
      await axios.delete(`${API_BASE}/user/${userInfo.id}/deleteImage`);
      toast.current?.show({
        severity: "success",
        summary: "Delete Success",
        detail: "Your ID image has been deleted.",
      });
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Delete Error",
        detail:
          error?.response?.data?.message ||
          "Failed to delete image on the server.",
      });
    }
  };

  const isAllValid =
    rules.length &&
    rules.upper &&
    rules.lower &&
    rules.number &&
    rules.special &&
    rules.notReuse &&
    rules.confirmMatch &&
    !!currentPassword;

  const handlePasswordSubmit = async () => {
    if (!isAllValid || loading) return;
    setLoading(true);

    try {
      await axios.patch(`${API_BASE}/user/password`, {
        id: userInfo.id,
        password: newPassword,
      });

      toast.current?.show({
        severity: "success",
        summary: "Password Updated",
        detail: "Your password has been changed successfully.",
      });

      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setShowCurrent(false);
      setShowNew(false);
      setShowConfirm(false);
      dispatch(logout());
      navigate("/signin");
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Update Failed",
        detail:
          error?.response?.data?.message ||
          "Server error. Please try again later.",
      });
    } finally {
      setLoading(false);
    }
  };

  const RuleItem = ({ ok, text }) => (
    <li
      className={ok ? "text-success" : "text-danger"}
      style={{ listStyle: "none" }}
    >
      {ok ? "✓" : "•"} {text}
    </li>
  );

  return (
    <div>
      <Header />
      <PageTitle />
      <div className="settings mb-80">
        <div className="container">
          <div className="row">
            <div className="col-xl-3 col-md-4">
              <SettingMenu />
            </div>
            <div className="col-xl-9 col-md-8">
              <div className="card">
                <div className="card-header">
                  <h4 className="card-title">{t("security.label")}</h4>
                </div>
                <div className="card-body">
                  <div className="row align-items-center">
                    <div className="col-xl-4">
                      <div className="id_card">
                        <img src={id} alt="" className="img-fluid" />
                      </div>
                    </div>
                    <div className="col-xl-6">
                      <div className="id_info">
                        <h3>{t("id.upload.label")}</h3>
                        <p className="mb-1 mt-3">
                          {t("id.upload.description")}
                        </p>
                        <p className="mb-1">
                          Status:{" "}
                          <span className="font-weight-bold">
                            {userInfo?.image_url ? "Uploaded" : "Not Uploaded"}
                          </span>
                        </p>
                        <Button
                          color="primary"
                          onClick={toggleModal}
                          className="mr-2"
                        >
                          New ID
                        </Button>{" "}
                        <Button color="danger" onClick={handleDeleteImage}>
                          Delete ID
                        </Button>
                      </div>
                    </div>
                  </div>

                  <Modal isOpen={modalOpen} toggle={toggleModal}>
                    <ModalHeader toggle={toggleModal}>
                      Upload New ID
                    </ModalHeader>
                    <ModalBody>
                      <div className="form-group">
                        <label htmlFor="id-upload-input">
                          Choose image file
                        </label>
                        <input
                          id="id-upload-input"
                          type="file"
                          accept="image/*"
                          className="form-control"
                          onChange={handleFileChange}
                        />
                      </div>

                      {previewUrl && (
                        <div className="mt-3 text-center">
                          <p className="mb-2">Preview:</p>
                          <img
                            src={previewUrl}
                            alt="ID preview"
                            style={{
                              maxWidth: "100%",
                              maxHeight: 250,
                              objectFit: "contain",
                            }}
                          />
                        </div>
                      )}
                    </ModalBody>
                    <ModalFooter>
                      <Button
                        color="primary"
                        onClick={submitImageFile}
                        disabled={!selectedFile}
                      >
                        Submit
                      </Button>
                      <Button color="secondary" onClick={toggleModal}>
                        Cancel
                      </Button>
                    </ModalFooter>
                  </Modal>

                  <Toast ref={toast} />
                </div>
              </div>

              <div className="card mt-4">
                <div className="card-header">
                  <h4 className="card-title">Change Password</h4>
                </div>
                <div className="card-body">
                  <div className="form-group">
                    <label>
                      {t("current.password.label", {
                        defaultValue: "Current Password",
                      })}
                    </label>
                    <div className="input-group">
                      <input
                        type={showCurrent ? "text" : "password"}
                        className="form-control"
                        placeholder="Enter current password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        autoComplete="current-password"
                      />
                      <div className="input-group-append">
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => setShowCurrent((s) => !s)}
                        >
                          {showCurrent ? "Hide" : "Show"}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>
                      {t("password.label", { defaultValue: "New Password" })}
                    </label>
                    <div className="input-group">
                      <input
                        type={showNew ? "text" : "password"}
                        className="form-control"
                        placeholder="At least 8 chars with upper/lower/number/special"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        autoComplete="new-password"
                      />
                      <div className="input-group-append">
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => setShowNew((s) => !s)}
                        >
                          {showNew ? "Hide" : "Show"}
                        </button>
                      </div>
                    </div>

                    <ul className="mt-2 mb-0 p-0">
                      <RuleItem
                        ok={rules.length}
                        text="At least 8 characters"
                      />
                      <RuleItem
                        ok={rules.upper}
                        text="Contains uppercase letter"
                      />
                      <RuleItem
                        ok={rules.lower}
                        text="Contains lowercase letter"
                      />
                      <RuleItem ok={rules.number} text="Contains number" />
                      <RuleItem
                        ok={rules.special}
                        text="Contains special character"
                      />
                      <RuleItem
                        ok={!!rules.notReuse}
                        text="Not the same as current password"
                      />
                    </ul>
                  </div>

                  <div className="form-group">
                    <label>
                      {t("confirm.password.label", {
                        defaultValue: "Confirm Password",
                      })}
                    </label>
                    <div className="input-group">
                      <input
                        type={showConfirm ? "text" : "password"}
                        className="form-control"
                        placeholder="Re-enter new password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        autoComplete="new-password"
                      />
                      <div className="input-group-append">
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => setShowConfirm((s) => !s)}
                        >
                          {showConfirm ? "Hide" : "Show"}
                        </button>
                      </div>
                    </div>
                    {!rules.confirmMatch && confirmPassword && (
                      <small className="text-danger">
                        Passwords do not match
                      </small>
                    )}
                  </div>

                  <div className="col-12">
                    <button
                      className="btn btn-success"
                      onClick={handlePasswordSubmit}
                      disabled={!isAllValid || loading}
                    >
                      {loading ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsSecurity;
